<script setup lang="ts">
// SideNavButton 1 2 3 are created to dynamically create the side navigation bar
import { computed, onBeforeUnmount, provide, ref, watch } from "vue";
import { PermissionNames } from "~/configs/permission_names";
import DesktopIntegration from "~/services/DesktopIntegration";
import SideNavButton from "~/types/sideNavButton";
import { isUserPermitted } from "~/utils/permissionHelper";
import { useAuthStore } from "~/stores/auth";
import { useConfigStore } from "~/stores/configStore";
import { useIsMobile } from "~/composable/useIsMobile";

const demoAiShow = useRuntimeConfig().public.demoAiShow;
const demoSentimentAnalysis = useRuntimeConfig().public.demoSentimentAnalysis;
const authStore = useAuthStore();
const configStore = useConfigStore();

const callCenterNavs = {
  parentName: "Call Center",
  imgName: "call-histories",
  children: [
    {
      name: "Dashboard",
      imgName: "dashboard",
      path: "/call-center/dashboard",
      permissionName: PermissionNames.CC_DASHBOARD_VIEW,
    },

    {
      name: "Call Histories",
      imgName: "call-histories",
      path: "/call-center/call-histories",
      permissionName: PermissionNames.CALL_HISTORY_SHOW,
    },
    {
      name: "Unhandled Calls",
      imgName: "unhandled-calls",
      path: "/call-center/unhandled-calls",
      permissionName: PermissionNames.UNHANDLED_CALL_HISTORY_SHOW,
    },
    {
      name: "Call Groups",
      imgName: "call-groups",
      path: "/call-center/call-groups",
      permissionName: PermissionNames.CALL_GROUP_SHOW,
    },
    {
      name: "Reports",
      imgName: "reports",
      path: "/call-center/report",
      permissionName: PermissionNames.CC_REPORT_GENERATE,
    },
  ],
};
const orderManagerNav = {
  parentName: "Order Manager",
  imgName: "order",
  children: [
    {
      name: "Order Manager",
      imgName: "order",
      path: "/order-manager/orders",
      permissionName: PermissionNames.ORDERS_SHOW,
    },
    {
      name: "Delivery Integration",
      imgName: "channel-automation",
      path: "/order-manager/integration",
      permissionName: PermissionNames.ORDERS_SHOW,
    },
    {
      name: "Website Integration",
      imgName: "channel-automation",
      path: "/order-manager/website-integrations",
      permissionName: PermissionNames.ORDERS_SHOW,
    },
    {
      name: "Order Automations",
      imgName: "channel-automation",
      path: "/order-manager/order-automations",
      permissionName: PermissionNames.ORDER_AUTOMATION_SHOW,
    },
    {
      name: "Reports ",
      imgName: "reports",
      path: "/order-manager/reports",
      permissionName: PermissionNames.ORDERS_SHOW,
    },
  ],
};
const teleSalesNav = {
  parentName: "Tele Sales",
  imgName: "tele-sales",
  children: [
    {
      name: "Tele Sales",
      imgName: "tele-sales",
      path: "/tele-sales/customers",
      permissionName: PermissionNames.TELE_SALES_CUSTOMER_SHOW,
    },
    {
      name: "Auto Dialer",
      imgName: "tele-sales",
      path: "/tele-sales/auto-dialer",
      permissionName: PermissionNames.AUTO_DIAL_SHOW,
    },
    {
      name: "Dial Session",
      imgName: "call-histories",
      path: "/tele-sales/auto-dialer/dial-session",
      permissionName: PermissionNames.AUTO_DIAL_SHOW,
    },
  ],
};

const taskManager = {
  parentName: "Task Manager",
  imgName: "tasks",
  children: [
    {
      name: "Dashboard",
      imgName: "tasks",
      path: "/task-manager/summary",
      permissionName: PermissionNames.TASK_DASHBOARD_VIEW,
    },
    {
      name: "Task Manager",
      imgName: "tasks",
      path: "/task-manager/tasks",
      permissionName: PermissionNames.TASK_SHOW,
    },
    {
      name: "Reports",
      imgName: "reports",
      path: "/task-manager/tasks/reports",
      permissionName: PermissionNames.TASK_SHOW,
    },
  ],
};

const side3: SideNavButton[] = [
  {
    name: "Settings",
    imgName: "settings",
    path: "settings",
    permissionName: PermissionNames.SETTING_MANAGE,
  },
];

if (demoSentimentAnalysis) {
  side3.push({
    name: "Sentiment Analysis",
    imgName: "kpi",
    path: "sentiment-analysis",
  });
}
if (demoAiShow) {
  side3.push({
    name: "Auto Reply Setup",
    imgName: "ai",
    path: "ai",
  });
}

/*
 * Home is one row or two, and which it is depends on `main-dashboard-view`.
 *
 * Without the dashboard there is only one destination up here, and a menu that
 * opens onto a single item is a click for nothing — so Home stays the flat row
 * it has always been. With the dashboard there are two, and they belong
 * together: both are "where I land", not two unrelated products. So Home
 * becomes the group and the pages hang under it as My Work and Dashboard,
 * rather than a second top-level row competing with the first.
 *
 * The children keep their own permissions either way, so the split in the
 * backend — Home behind `main-home-view`, the dashboard behind
 * `main-dashboard-view` — still decides what is actually reachable. What is on
 * the dashboard is filtered again per module inside the page.
 *
 * Group children carry their leading slash; a flat entry is linked as
 * `/${path}` and must not.
 */
const homeItem: SideNavButton = {
  name: "Home",
  imgName: "dashboard",
  path: "home",
  permissionName: PermissionNames.MAIN_HOME_VIEW,
};

const homeNavs = {
  parentName: "Home",
  imgName: "dashboard",
  children: [
    {
      // The page the flat row used to open, renamed only where it sits beside
      // the dashboard — on its own it is still just "Home".
      name: "My Work",
      imgName: "dashboard",
      path: "/home",
      permissionName: PermissionNames.MAIN_HOME_VIEW,
    },
    {
      name: "Dashboard",
      imgName: "dashboard-grid",
      path: "/dashboard",
      permissionName: PermissionNames.MAIN_DASHBOARD_VIEW,
    },
  ],
};
// Computed because globalConfig loads asynchronously — a plain const would
// snapshot the empty config and never show/hide Scheduled Messages.
const helpDeskNavs = computed(() => ({
  parentName: "Help Desk",
  imgName: "opportunities",
  children: [
    {
      name: "Dashboard",
      imgName: "dashboard",
      path: "/help-desk/dashboard",
      permissionName: PermissionNames.HELP_DESK_DASHBOARD_VIEW,
    },
    {
      name: "Tickets",
      imgName: "tickets",
      path: "/help-desk/ticket-list",
      permissionName: PermissionNames.TICKET_SHOW,
    },
    {
      name: "Inbox",
      imgName: "inbox",
      path: "/help-desk/inbox",
      permissionName: PermissionNames.TICKET_INBOX_SHOW,
    },
    {
      name: "AI Tickets",
      imgName: "tickets",
      path: "/help-desk/automation-tickets",
      permissionName: PermissionNames.AI_TICKET_SHOW,
    },

    {
      name: "Gallery",
      imgName: "pages",
      path: "/help-desk/gallery",
      permissionName: PermissionNames.TICKET_INBOX_SHOW,
    },
    {
      name: "Meta Integration",
      imgName: "pages",
      path: "/help-desk/pages",
      permissionName: PermissionNames.PAGE_SHOW,
    },
    {
      name: "Reply Templates",
      imgName: "message-templates",
      path: "/help-desk/reply/templates",
      permissionName: PermissionNames.REPLY_TEMPLATE_SHOW,
    },
    {
      name: "WhatsApp Campaign",
      imgName: "sms-histories",
      path: "/help-desk/whatsapp-campaigns",
      permissionName: PermissionNames.WHATSAPP_CAMPAIGN_SHOW,
    },
    ...(configStore.globalConfig.help_desk?.ticket_scheduled_message_enabled
      ? [
          {
            name: "Scheduled Messages",
            imgName: "activities",
            path: "/help-desk/scheduled-message",
            permissionName: PermissionNames.TICKET_SHOW,
          },
        ]
      : []),
    {
      name: "AI Agents",
      imgName: "ai",
      path: "/help-desk/automations",
      permissionName: PermissionNames.AI_AGENT_SHOW,
    },

    {
      name: "Reports",
      imgName: "pages",
      path: "/help-desk/reports",
      permissionName: PermissionNames.HELP_DESK_REPORT_GENERATE,
    },
  ],
}));

const crmNavs = {
  parentName: "CRM",
  imgName: "crm",
  children: [
    {
      name: "Dashboard",
      imgName: "dashboard",
      path: "/crm/dashboard",
      permissionName: PermissionNames.CRM_DASHBOARD_VIEW,
    },
    {
      name: "Leads",
      imgName: "leads",
      path: "/crm/leads",
      permissionName: PermissionNames.LEAD_SHOW,
    },
    {
      name: "Leads Stages",
      imgName: "leads",
      path: "/crm/leads/status/manage",
      permissionName: PermissionNames.LEAD_STATUS_SHOW,
    },
    // Each inbox is only there while its way in is switched on in settings.
    ...(configStore.globalConfig.crm?.lead_form_submission_enabled !== false
      ? [
          {
            name: "CF Submission",
            imgName: "leads",
            path: "/crm/request-submissions",
            permissionName:
              PermissionNames.LEAD_CONFIRMATION_FORM_SUBMISSION_SHOW,
          },
        ]
      : []),
    ...(configStore.globalConfig.crm?.lead_public_form_enabled !== false
      ? [
          {
            name: "PF Submissions",
            imgName: "leads",
            path: "/crm/lead-public-form-submissions",
            permissionName: PermissionNames.LEAD_PUBLIC_FORM_SUBMISSION_SHOW,
          },
        ]
      : []),
    ...(configStore.globalConfig.crm?.lead_external_enabled
      ? [
          {
            name: "API Submissions",
            imgName: "leads",
            path: "/crm/external-lead-submissions",
            permissionName: PermissionNames.EXTERNAL_LEAD_SUBMISSION_SHOW,
          },
        ]
      : []),
    {
      name: "Opportunities",
      imgName: "opportunities",
      path: "/crm/opportunities",
      permissionName: PermissionNames.OPPORTUNITY_SHOW,
    },
    {
      name: "Opportunities Stages",
      imgName: "opportunities",
      path: "/crm/opportunities/stages/manage",
      permissionName: PermissionNames.OPPORTUNITY_STAGE_SHOW,
    },
    {
      name: "Organizations",
      imgName: "organizations",
      path: "/crm/organizations",
      permissionName: PermissionNames.ORGANIZATION_SHOW,
    },
    {
      name: "Partners",
      imgName: "organizations",
      path: "/crm/partners",
      permissionName: PermissionNames.ORGANIZATION_SHOW,
    },
    {
      name: "Quotes",
      imgName: "quotes",
      path: "/crm/quotes",
      permissionName: PermissionNames.QUOTE_SHOW,
    },
    {
      name: "Targets",
      imgName: "kpi",
      path: "/crm/targets",
      permissionName: PermissionNames.TARGET_SHOW,
    },
    {
      name: "Reports",
      imgName: "reports",
      path: "/crm/reports",
      permissionName: PermissionNames.CRM_REPORT_GENERATE,
    },
    {
      name: "Discount Requests",
      imgName: "reports",
      path: "/crm/discount-requests",
      permissionName: PermissionNames.DISCOUNT_REQUEST_SHOW,
    },
  ],
};

const profileNavs = {
  parentName: "Profiles",
  imgName: "profiles",
  children: [
    {
      name: "Dashboard",
      imgName: "dashboard",
      path: "/profiles/dashboard",
      permissionName: PermissionNames.CRM_DASHBOARD_VIEW,
    },
    {
      name: "Profiles",
      imgName: "profiles",
      path: "/profiles",
      permissionName: PermissionNames.PROFILE_SHOW,
    },
    {
      name: "Reports",
      imgName: "reports",
      path: "/profiles/reports",
      permissionName: PermissionNames.CRM_REPORT_GENERATE,
    },
  ],
};
const usersNavs = {
  parentName: "Users",
  imgName: "users",
  children: [
    {
      name: "Users",
      imgName: "users",
      path: "/users",
      permissionName: PermissionNames.USER_SHOW,
    },
    {
      // Was a top-level rail entry. Its path carries the leading slash the
      // other children have — a flat entry is linked as `/${path}`, a child
      // as-is — so moving it without that slash would have broken the link.
      name: "Roles",
      imgName: "roles",
      path: "/roles",
      permissionName: PermissionNames.ROLE_SHOW,
    },
    {
      name: "Reports",
      imgName: "reports",
      path: "/users/reports",
      permissionName: PermissionNames.USER_SHOW,
    },
  ],
};
const productsNav = {
  parentName: "Products",
  imgName: "products",
  children: [
    {
      name: "Products",
      imgName: "products",
      path: "/products",
      permissionName: PermissionNames.PRODUCT_SHOW,
    },
    {
      name: "Payment Items",
      imgName: "products",
      path: "/crm/payment-items",
      permissionName: PermissionNames.PAYMENT_ITEM_SHOW,
    },
    /*
     * Stock sits under Products because that is where somebody goes looking
     * for it — it is the same catalogue, asked a different question. Its own
     * permission, so a storekeeper can be given the stock screens without the
     * catalogue and an agent can keep the catalogue without the stock.
     */
    {
      name: "Stock",
      imgName: "products",
      path: "/stock",
      permissionName: PermissionNames.STOCK_SHOW,
    },
  ],
};
const smsNav = {
  parentName: "SMS",
  imgName: "sms-histories",
  children: [
    {
      name: "SMS Histories",
      imgName: "sms-histories",
      path: "/sms-histories",
      permissionName: PermissionNames.SMS_HISTORY_SHOW,
    },
    {
      name: "Balance",
      imgName: "sms-histories",
      path: "/sms-histories/balance",
      permissionName: PermissionNames.SMS_HISTORY_SHOW,
    },
    {
      name: "Reports",
      imgName: "reports",
      path: "/sms-histories/reports",
      permissionName: PermissionNames.SMS_HISTORY_SHOW,
    },
  ],
};

const desktopIntegration = inject("desktopIntegration") as DesktopIntegration;

const isShowAble = function (permission?: PermissionNames) {
  if (!permission) return true;
  let condition = isUserPermitted(permission);
  if (permission === PermissionNames.SETTING_MANAGE) {
    condition = condition || desktopIntegration.isDesktopApp();
  }

  return condition;
};

interface NavGroup {
  parentName: string;
  imgName: string;
  children: {
    name: string;
    imgName?: string;
    path: string;
    permissionName?: PermissionNames;
  }[];
}

interface NavEntry {
  key: string;
  item?: SideNavButton;
  group?: NavGroup;
}

// Single source of truth for what this rail shows: the template renders it and
// quick search searches it, so a destination can never turn up in the search
// results that the user is not allowed to reach from the rail itself. Computed
// rather than a const because both the feature flags and helpDeskNavs resolve
// asynchronously.
const navEntries = computed<NavEntry[]>(() => {
  const entries: NavEntry[] = [];

  const pushItems = (items: SideNavButton[]) => {
    items.forEach((item) => {
      if (isShowAble(item.permissionName)) {
        entries.push({ key: `item-${item.path}`, item });
      }
    });
  };

  /*
   * One row or a group — see `homeNavs`. The group is gated on the dashboard
   * permission rather than on "has any permitted child", because the dashboard
   * is the only thing that can put a second item in it: with just Home there
   * would be a menu holding one link.
   */
  if (isUserPermitted(PermissionNames.MAIN_DASHBOARD_VIEW)) {
    entries.push({ key: `group-${homeNavs.parentName}`, group: homeNavs });
  } else {
    pushItems([homeItem]);
  }

  // Order matters — this is the order the rail renders in.
  const groups: [boolean, NavGroup][] = [
    [authStore.features.crm, crmNavs],
    [authStore.features.call_center, callCenterNavs],
    [authStore.features.help_desk, helpDeskNavs.value],
    [authStore.features.tele_sales, teleSalesNav],
    [authStore.features.order_manager, orderManagerNav],
    [authStore.features.task_manager, taskManager],
    [isUserPermitted(PermissionNames.PROFILE_SHOW), profileNavs],
    /*
     * Roles lives in here now, so the group has to open for an account that can
     * see roles but not users — otherwise moving the entry would have quietly
     * taken Roles away from exactly the people who only had that. The children
     * keep their own permissions, so such an account gets the group with Roles
     * alone inside it.
     */
    [
      isUserPermitted(PermissionNames.USER_SHOW) ||
        isUserPermitted(PermissionNames.ROLE_SHOW),
      usersNavs,
    ],
    [isUserPermitted(PermissionNames.SMS_HISTORY_SHOW), smsNav],
    [isUserPermitted(PermissionNames.PRODUCTS_SHOW), productsNav],
  ];
  groups.forEach(([enabled, group]) => {
    if (!enabled) return;

    /*
     * A group with nothing the user may open is not a menu, it is a row that
     * answers a hover with an empty box. It matters here because the gate above
     * is not always as tight as it looks: `PermissionNames.PRODUCTS_SHOW` does
     * not exist on the enum (it is PRODUCT_SHOW), so that entry reads as
     * undefined and isUserPermitted lets everyone through. Rather than change
     * who can see Products — which would take the menu from whoever relies on
     * it today — the group simply has to have something in it.
     */
    const hasChildren = group.children.some((child) =>
      isUserPermitted(child.permissionName),
    );

    if (hasChildren) entries.push({ key: `group-${group.parentName}`, group });
  });

  pushItems(side3);

  return entries;
});

// Flattened for quick search. Flat rail items link to `/${path}` while group
// children already carry their leading slash — resolving that here keeps the
// rule in one place instead of leaving it to the search component.
const destinations = computed(() =>
  navEntries.value.flatMap((entry) => {
    if (entry.item) {
      return [
        {
          key: entry.key,
          name: entry.item.name,
          parentName: "",
          to: `/${entry.item.path}`,
          imgName: entry.item.imgName,
        },
      ];
    }
    const group = entry.group as NavGroup;
    return group.children
      .filter((child) => isUserPermitted(child.permissionName))
      .map((child) => ({
        key: `${group.parentName}-${child.path}`,
        name: child.name,
        parentName: group.parentName,
        to: child.path,
        imgName: child.imgName || group.imgName,
      }));
  }),
);

// Hover-expand is a desktop affordance only; 639px matches the `sm` breakpoint
// the rail itself switches on, so the two can never disagree.
const isMobile = useIsMobile(639);

const panelEl = ref<HTMLElement | null>(null);
const isPointerInside = ref(false);
const isFocusInside = ref(false);
const searchQuery = ref("");
// Only one group flyout may be open at a time across the whole rail.
const openFlyout = ref<string | null>(null);

const isExpanded = computed(
  () => !isMobile.value && (isPointerInside.value || isFocusInside.value),
);

provide("sideNavExpanded", isExpanded);
provide("sideNavOpenFlyout", openFlyout);

let expandTimer: ReturnType<typeof setTimeout> | null = null;
let collapseTimer: ReturnType<typeof setTimeout> | null = null;

const clearTimers = () => {
  if (expandTimer) {
    clearTimeout(expandTimer);
    expandTimer = null;
  }
  if (collapseTimer) {
    clearTimeout(collapseTimer);
    collapseTimer = null;
  }
};

// A pointer merely crossing the rail on its way somewhere else should not throw
// the panel open, and a pointer cutting the corner on its way to a flyout
// should not slam it shut — hence the asymmetric delays.
const handlePointerEnter = () => {
  clearTimers();
  expandTimer = setTimeout(() => {
    isPointerInside.value = true;
  }, 70);
};

const handlePointerLeave = () => {
  clearTimers();
  collapseTimer = setTimeout(() => {
    isPointerInside.value = false;
  }, 180);
};

// Tab-navigation has to be visible, so keyboard focus anywhere inside the panel
// (including a flyout, which stays a DOM child of its row) holds it open. Mouse
// focus deliberately does not: clicking a link leaves the focus sitting on it,
// which would pin the rail open long after the pointer had gone. :focus-visible
// is exactly that distinction, and it still treats a clicked text input as
// keyboard focus — which is what the search box needs.
const isKeyboardFocus = (target: HTMLElement | null) => {
  if (!target?.matches) return false;
  try {
    return target.matches(":focus-visible");
  } catch {
    return true;
  }
};

const handleFocusIn = (event: FocusEvent) => {
  isFocusInside.value = isKeyboardFocus(event.target as HTMLElement | null);
};

const handleFocusOut = (event: FocusEvent) => {
  const next = event.relatedTarget as Node | null;
  if (next && panelEl.value?.contains(next)) return;
  isFocusInside.value = false;
};

// A query left behind on collapse would hide the icon list behind results the
// user can no longer read.
watch(isExpanded, (expanded) => {
  if (!expanded) {
    searchQuery.value = "";
    openFlyout.value = null;
  }
});

const isSearching = computed(
  () => isExpanded.value && searchQuery.value.trim().length > 0,
);

// Results replace the icon list, and a flyout hanging off a row that is no
// longer on screen would be left stranded beside it.
watch(isSearching, (searching) => {
  if (searching) openFlyout.value = null;
});

onBeforeUnmount(clearTimers);
</script>
<template>
  <!-- The root keeps its collapsed width and reserves the column in the layout
       flex row; the panel inside is fixed, so expanding it overlays the page
       instead of pushing a single pixel of content sideways. -->
  <div
    class="side-nav-root sm:bg-light-purple sm:min-h-[100vh] z-[120] sm:w-[4.5rem] 2xl:w-[6rem] sm:shrink-0"
  >
    <div
      ref="panelEl"
      class="sidebar side-nav-panel p-2 pt-0 gap-3 flex flex-col"
      :class="{ 'is-expanded': isExpanded }"
      @mouseenter="handlePointerEnter"
      @mouseleave="handlePointerLeave"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
    >
      <!--SmartComm logo goes here-->
      <div class="side-nav-header hidden sm:flex">
        <img
          alt=""
          class="side-nav-logo 2xl:min-w-[90px] min-w-[80px] object-cover mt-0"
          src="/images/logo_3.png"
        />
      </div>

      <!--SideNav Button goes here-->
      <!-- Search and list share the one scroll area so a long result list can
           never spill out of the panel, which clips its own overflow. -->
      <div class="side-nav-scroll">
        <!-- Desktop panel only: SideNavSearch hides itself below sm, so the
             mobile "More" sheet keeps its plain list. -->
        <SideNavSearch
          v-model="searchQuery"
          :destinations="destinations"
          :expanded="isExpanded"
          @expand="isFocusInside = true"
        />
        <div v-show="!isSearching" class="flex flex-col gap-2">
          <template v-for="entry in navEntries" :key="entry.key">
            <SideIcon v-if="entry.item" :side-nav-data="entry.item" />
            <SideIconWithChild
              v-else-if="entry.group"
              :side-nav-data="entry.group"
            />
          </template>
        </div>
      </div>
      <!--SideNav Button ends here-->
    </div>
  </div>
</template>
<style scoped>
.side-nav-root {
  /* Kept in step with the root's own sm:w-[4.5rem] / 2xl:w-[6rem]: the fixed
     panel cannot inherit that width as a percentage, so it reads these. */
  --rail-width: 4.5rem;
  --rail-expanded-width: 16rem;
  /*
    One duration and one curve for every moving part of the rail. They used to
    disagree — 200ms on a width next to 150ms on the opacity beside it — so the
    labels landed before the panel did and the whole thing read as two
    animations arguing rather than one panel opening.
  */
  --rail-motion: 200ms;
  --rail-ease: cubic-bezier(0.4, 0, 0.2, 1);
}

@media (min-width: 1536px) {
  .side-nav-root {
    --rail-width: 6rem;
  }
}

.sidebar {
  position: -webkit-sticky;
  /* Safari */
  position: sticky;
  top: var(--client-banner-height, 0px);
  z-index: 999;
  left: 0;
}

@media (min-width: 640px) {
  /* Fixed rather than sticky: the panel has to grow over the page content, and
     it has to keep the top bar's banner offset while the page scrolls. No
     transform is used anywhere on it — that would make it the containing block
     for the flyouts, which rely on being fixed to escape the scroll area. */
  .side-nav-panel {
    position: fixed;
    top: var(--client-banner-height, 0px);
    bottom: 0;
    left: 0;
    width: var(--rail-width);
    background-color: var(--light-purple);
    /*
      Deliberately not `overflow: hidden`. The logo is wider than the collapsed
      rail and always has been — it overspilled the 4.5rem column in the old
      markup too — so clipping here is what made it look shrunken. The list
      below clips its own overflow, which is all the labels ever needed.

      No will-change either: it is tempting on an animating width, but several
      of its values turn an element into the containing block for fixed
      descendants, and the flyouts depend on this one never becoming that.
    */
    transition:
      width var(--rail-motion) var(--rail-ease),
      box-shadow var(--rail-motion) var(--rail-ease);
  }

  .dark .side-nav-panel {
    background-color: var(--dark-blue);
  }

  .side-nav-panel.is-expanded {
    width: var(--rail-expanded-width);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  }

  /*
    Centred on the COLLAPSED rail width in both states, not on the panel.
    Centring on the panel would walk the logo eighty pixels to the right every
    time somebody grazed the rail — a lot of movement to set off with one
    hover, and it would stop sitting where it has always sat.
  */
  .side-nav-header {
    /*
      The panel's CONTENT box at the collapsed width — the rail minus its own
      p-2 on each side. That is the box the logo used to be centred in, back
      when `.sidebar` was `sm:items-center` and this was the only child, and
      the logo has always been a little wider than it (80px in a 56px box, 90px
      in an 80px box at 2xl) and overspilled evenly on both sides.

      Measuring against the full rail width instead put the centre 8px to the
      right of where it had always been, which is the lean the logo picked up.
      Fixed to the collapsed width in both states so a hover does not walk it.
    */
    width: calc(var(--rail-width) - 1rem);
    flex: none;
    align-items: center;
    justify-content: center;
  }

  .side-nav-logo {
    height: auto;
    flex: none;
  }

  /* ~14 top-level entries with every feature on: the list, not the page, owns
     the overflow. */
  .side-nav-scroll {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: rgb(255 255 255 / 0.16) transparent;
  }
}

/* For WebKit browsers (Chrome, Safari) */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0);
  border-radius: 4px;
}

/* Semi-transparent on hover — light on the rail, which is dark in both themes. */
::-webkit-scrollbar-thumb:hover {
  background-color: rgb(255 255 255 / 0.25);
}
</style>
